import { useEffect, useRef } from 'react'

function RemoveDeviceModal({ isDarkMode, isRemoving, error, onConfirm, onClose }) {
	const cancelRef = useRef(null)

	useEffect(() => {
		cancelRef.current?.focus()
	}, [])

	// Escape closes it, the way a dialog is expected to behave. Ignored while the
	// request is in flight so the confirmation cannot be dismissed mid-removal.
	useEffect(() => {
		const onKeyDown = event => {
			if (event.key === 'Escape' && !isRemoving) onClose()
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [isRemoving, onClose])

	return (
		<div className="relative z-50" role="dialog" aria-modal="true" aria-labelledby="remove-device-title">
			<button
				type="button"
				tabIndex={-1}
				aria-hidden="true"
				onClick={() => !isRemoving && onClose()}
				className="fixed inset-0 w-full cursor-default bg-gray-900/50 transition-opacity"
			/>
			<div className="pointer-events-none fixed inset-0 z-10 flex items-start justify-center p-4 pt-24">
				<div className="pointer-events-auto w-full max-w-sm">
					<div
						className={`transform overflow-hidden rounded-lg px-5 py-5 shadow-xl ${
							isDarkMode
								? 'bg-gray-800 outline outline-1 -outline-offset-1 outline-white/10'
								: 'bg-white'
						}`}
					>
						<h3
							id="remove-device-title"
							className={`text-base font-semibold ${
								isDarkMode ? 'text-white' : 'text-gray-900'
							}`}
						>
							Are you sure?
						</h3>
						<p
							className={`mt-1 text-sm ${
								isDarkMode ? 'text-gray-300' : 'text-gray-500'
							}`}
						>
							This device will be signed out.
						</p>

						{error && <p className="mt-3 text-sm text-red-500">{error}</p>}

						<div className="mt-5 flex justify-end gap-2">
							<button
								ref={cancelRef}
								type="button"
								onClick={onClose}
								disabled={isRemoving}
								className={`rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
									isDarkMode
										? 'bg-gray-700 text-white hover:bg-gray-600'
										: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
								}`}
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={onConfirm}
								disabled={isRemoving}
								className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
							>
								{isRemoving ? 'Signing out...' : 'Sign out'}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default RemoveDeviceModal
